import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Switch,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  ListItemText,
  Grid,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Bolt as TestIcon,
  History as LogsIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { formatRelativeTime } from '../../utils/dateUtils';

const serverMessage = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;

const SECRET_FIELDS = ['secret', 'apiToken', 'apiKey', 'clientSecret'];

const TYPE_LABELS = { webhook: 'Webhook', splunk: 'Splunk HEC', elastic: 'Elasticsearch' };
const TYPE_COLORS = { webhook: 'default', splunk: 'warning', elastic: 'info' };

const truncate = (value, max = 46) => {
  const s = String(value || '');
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

const emptyForm = {
  name: '',
  type: 'webhook',
  url: '',
  enabled: true,
  events: [],
  secret: '',
  apiToken: '',
  apiKey: '',
  username: '',
  clientSecret: '',
  index: '',
  sourcetype: '',
  rpm: 120,
  headers: [],
  cleared: {},
};

const formFromDestination = (d) => ({
  ...emptyForm,
  name: d.name || '',
  type: d.type || 'webhook',
  url: d.url || '',
  enabled: d.enabled !== false,
  events: Array.isArray(d.events) ? d.events : [],
  username: d.username || '',
  index: d.index || '',
  sourcetype: d.sourcetype || '',
  rpm: d.rpm || 120,
  headers: Object.entries(d.headers || {}).map(([key, value]) => ({ key, value: String(value) })),
  cleared: {},
});

const headersToObject = (rows) => {
  const out = {};
  rows.forEach(({ key, value }) => {
    const k = String(key || '').trim();
    if (k) out[k] = String(value || '');
  });
  return out;
};

const OutboundDestinations = ({ readOnly = false }) => {
  const [destinations, setDestinations] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [logsOpen, setLogsOpen] = useState(false);
  const [logsTitle, setLogsTitle] = useState('Delivery log');
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.integrations.getDestinations();
      setDestinations(res.data?.destinations || []);
      setTypeOptions(res.data?.types || []);
      setEventTypes(res.data?.events || []);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load outbound destinations'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(null), 4000);
  };

  const eventLabel = useMemo(() => {
    const map = {};
    eventTypes.forEach((e) => { map[e.key] = e.label; });
    return (key) => map[key] || key;
  }, [eventTypes]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (destination) => {
    setEditing(destination);
    setForm(formFromDestination(destination));
    setFormError(null);
    setDialogOpen(true);
  };

  const buildPayload = () => {
    const payload = {
      name: form.name.trim(),
      type: form.type,
      url: form.url.trim(),
      enabled: form.enabled,
      events: form.events,
      rpm: Number(form.rpm) || 120,
    };
    if (form.type === 'webhook') payload.headers = headersToObject(form.headers);
    if (form.type === 'splunk') {
      payload.index = form.index.trim();
      payload.sourcetype = form.sourcetype.trim() || 'excubia:event';
    }
    if (form.type === 'elastic') {
      payload.index = form.index.trim() || 'excubia-events';
      payload.username = form.username.trim();
    }
    SECRET_FIELDS.forEach((key) => {
      if (form.cleared[key]) { if (editing) payload[key] = null; return; }
      if (String(form[key] || '').trim()) payload[key] = String(form[key]).trim();
    });
    return payload;
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.url.trim()) {
      setFormError('Name and URL are required');
      return;
    }
    try {
      setSaving(true);
      setFormError(null);
      const payload = buildPayload();
      if (editing) await apiService.integrations.updateDestination(editing._id, payload);
      else await apiService.integrations.createDestination(payload);
      setDialogOpen(false);
      setEditing(null);
      flash(editing ? 'Destination updated' : 'Destination created');
      await load();
    } catch (err) {
      setFormError(serverMessage(err, 'Failed to save destination'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (destination, enabled) => {
    try {
      setBusyId(destination._id);
      setError(null);
      await apiService.integrations.updateDestination(destination._id, { enabled });
      await load();
    } catch (err) {
      setError(serverMessage(err, 'Failed to update destination'));
    } finally {
      setBusyId(null);
    }
  };

  const handleTest = async (destination) => {
    try {
      setBusyId(destination._id);
      setError(null);
      const res = await apiService.integrations.testDestination(destination._id);
      flash(`${destination.name}: test event delivered${res.data?.status ? ` (HTTP ${res.data.status})` : ''}`);
    } catch (err) {
      const data = err?.response?.data;
      setError(`${destination.name}: ${data?.error || data?.message || 'test delivery failed'}${data?.status ? ` (HTTP ${data.status})` : ''}`);
    } finally {
      setBusyId(null);
      load();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await apiService.integrations.deleteDestination(deleteTarget._id);
      setDeleteTarget(null);
      flash('Destination deleted');
      await load();
    } catch (err) {
      setError(serverMessage(err, 'Failed to delete destination'));
    } finally {
      setDeleting(false);
    }
  };

  const openLogs = async (destination) => {
    setLogsTitle(destination ? `Deliveries — ${destination.name}` : 'Recent deliveries');
    setLogsOpen(true);
    try {
      setLogsLoading(true);
      setLogs([]);
      const res = await apiService.integrations.getLogs({ integrationId: destination?._id, limit: 50 });
      setLogs(res.data?.logs || []);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load delivery log'));
    } finally {
      setLogsLoading(false);
    }
  };

  const secretHelper = (key, hasFlag, hint) => {
    if (form.cleared[key]) return 'Will be cleared when you save.';
    if (editing && hasFlag) return 'Configured — leave blank to keep the existing value.';
    return hint || '';
  };

  const renderSecretField = (key, label, hasFlag, hint) => (
    <Grid item xs={12} sm={editing && hasFlag ? 8 : 12}>
      <TextField
        fullWidth
        type="password"
        label={label}
        value={form[key]}
        onChange={(e) => setField(key, e.target.value)}
        helperText={secretHelper(key, hasFlag, hint)}
        disabled={readOnly || Boolean(form.cleared[key])}
      />
      {editing && hasFlag && (
        <Button
          size="small"
          color={form.cleared[key] ? 'primary' : 'error'}
          onClick={() => setForm((prev) => ({ ...prev, [key]: '', cleared: { ...prev.cleared, [key]: !prev.cleared[key] } }))}
          disabled={readOnly}
          sx={{ mt: 0.5 }}
        >
          {form.cleared[key] ? 'Undo clear' : 'Clear'}
        </Button>
      )}
    </Grid>
  );

  const setHeaderRow = (index, field, value) => setForm((prev) => ({
    ...prev,
    headers: prev.headers.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
  }));

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      <Card>
        <CardHeader
          avatar={<SendIcon />}
          title="Outbound destinations"
          subheader="Forward Excubia events to a webhook, Splunk HEC or Elasticsearch"
          action={
            <Box display="flex" gap={1}>
              <Tooltip title="Reload">
                <span>
                  <IconButton size="small" onClick={load} disabled={loading}><RefreshIcon /></IconButton>
                </span>
              </Tooltip>
              <Button size="small" startIcon={<LogsIcon />} onClick={() => openLogs(null)}>All logs</Button>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openAdd} disabled={readOnly}>
                Add destination
              </Button>
            </Box>
          }
        />
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
          ) : destinations.length === 0 ? (
            <Alert severity="info">
              No destinations configured. Add one to stream detections, cases, SLA breaches and automation results to your
              own tooling as they happen.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell>Events</TableCell>
                    <TableCell>Enabled</TableCell>
                    <TableCell>Deliveries</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {destinations.map((d) => {
                    const stats = d.stats || {};
                    return (
                      <TableRow key={d._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{d.name}</Typography>
                          {d.lastTest?.at && (
                            <Typography variant="caption" color={d.lastTest.success ? 'success.main' : 'error.main'}>
                              Last test {d.lastTest.success ? 'ok' : 'failed'} {formatRelativeTime(d.lastTest.at)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={TYPE_LABELS[d.type] || d.type} color={TYPE_COLORS[d.type] || 'default'} />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={d.url || ''}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{truncate(d.url)}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          {(d.events || []).length === 0 ? (
                            <Typography variant="caption" color="text.secondary">All events</Typography>
                          ) : (
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {d.events.map((e) => <Chip key={e} size="small" variant="outlined" label={eventLabel(e)} />)}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Switch
                            size="small"
                            checked={d.enabled !== false}
                            onChange={(e) => handleToggle(d, e.target.checked)}
                            disabled={readOnly || busyId === d._id}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">
                            {stats.deliveries || 0} sent · {stats.failures || 0} failed
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {stats.lastDeliveryAt ? formatRelativeTime(stats.lastDeliveryAt) : 'No deliveries yet'}
                          </Typography>
                          {stats.lastError && (
                            <Tooltip title={stats.lastError}>
                              <Chip size="small" color="error" variant="outlined" label={`Last error${stats.consecutiveFailures ? ` ×${stats.consecutiveFailures}` : ''}`} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Send a test event">
                            <span>
                              <IconButton size="small" onClick={() => handleTest(d)} disabled={readOnly || busyId === d._id}>
                                {busyId === d._id ? <CircularProgress size={16} /> : <TestIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(d)}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delivery log">
                            <IconButton size="small" onClick={() => openLogs(d)}><LogsIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <span>
                              <IconButton size="small" color="error" onClick={() => setDeleteTarget(d)} disabled={readOnly}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>Every event is posted as a single JSON object:</Typography>
            <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
              {'{ id, at, type, company: { id }, title, message, severity, link, data: { detection | case | rule | results } }'}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Splunk destinations wrap the same object in a HEC envelope; Elasticsearch destinations index it directly.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Add / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add destination'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {formError && <Grid item xs={12}><Alert severity="error">{formError}</Alert></Grid>}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Name"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={readOnly || Boolean(editing)}>
                <InputLabel>Type</InputLabel>
                <Select value={form.type} label="Type" onChange={(e) => setField('type', e.target.value)}>
                  {(typeOptions.length ? typeOptions : Object.entries(TYPE_LABELS).map(([key, label]) => ({ key, label }))).map((t) => (
                    <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label={form.type === 'splunk' ? 'HEC base URL' : form.type === 'elastic' ? 'Base URL' : 'Webhook URL'}
                value={form.url}
                onChange={(e) => setField('url', e.target.value)}
                placeholder={
                  form.type === 'splunk' ? 'https://splunk.example.com:8088'
                    : form.type === 'elastic' ? 'https://elastic.example.com:9200'
                      : 'https://hooks.example.com/excubia'
                }
                disabled={readOnly}
              />
            </Grid>

            {form.type === 'webhook' && (
              <>
                {renderSecretField('secret', 'Signing secret', editing?.hasSecret, 'Optional. Used to sign the request body.')}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    When a signing secret is set, each request carries{' '}
                    <code>X-Excubia-Signature: sha256=&lt;hex&gt;</code> — an HMAC-SHA256 of the raw JSON body using the secret.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="subtitle2">Extra headers (optional)</Typography>
                </Grid>
                {form.headers.map((row, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Header"
                        value={row.key}
                        onChange={(e) => setHeaderRow(index, 'key', e.target.value)}
                        disabled={readOnly}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Value"
                        value={row.value}
                        onChange={(e) => setHeaderRow(index, 'value', e.target.value)}
                        disabled={readOnly}
                      />
                    </Grid>
                    <Grid item xs={1} display="flex" alignItems="center">
                      <IconButton
                        size="small"
                        onClick={() => setForm((prev) => ({ ...prev, headers: prev.headers.filter((_, i) => i !== index) }))}
                        disabled={readOnly}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                  </React.Fragment>
                ))}
                <Grid item xs={12}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setForm((prev) => ({ ...prev, headers: [...prev.headers, { key: '', value: '' }] }))}
                    disabled={readOnly}
                  >
                    Add header
                  </Button>
                </Grid>
              </>
            )}

            {form.type === 'splunk' && (
              <>
                {renderSecretField('apiToken', 'HEC token', editing?.hasApiToken, 'The HTTP Event Collector token.')}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Index"
                    value={form.index}
                    onChange={(e) => setField('index', e.target.value)}
                    placeholder="main"
                    helperText="Optional — the token's default index is used when blank."
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Sourcetype"
                    value={form.sourcetype}
                    onChange={(e) => setField('sourcetype', e.target.value)}
                    placeholder="excubia:event"
                    helperText="Defaults to excubia:event"
                    disabled={readOnly}
                  />
                </Grid>
              </>
            )}

            {form.type === 'elastic' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Index"
                    value={form.index}
                    onChange={(e) => setField('index', e.target.value)}
                    placeholder="excubia-events"
                    helperText="Defaults to excubia-events"
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Authenticate with either an API key or a username and password.
                  </Typography>
                </Grid>
                {renderSecretField('apiKey', 'API key', editing?.hasApiKey, 'Base64 Elasticsearch API key.')}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={form.username}
                    onChange={(e) => setField('username', e.target.value)}
                    disabled={readOnly}
                  />
                </Grid>
                {renderSecretField('clientSecret', 'Password', editing?.hasClientSecret)}
              </>
            )}

            <Grid item xs={12}><Divider /></Grid>

            <Grid item xs={12}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Events</InputLabel>
                <Select
                  multiple
                  value={form.events}
                  label="Events"
                  onChange={(e) => setField('events', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => (
                    selected.length === 0
                      ? 'All events'
                      : (
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {selected.map((key) => <Chip key={key} size="small" label={eventLabel(key)} />)}
                        </Box>
                      )
                  )}
                >
                  {eventTypes.map((e) => (
                    <MenuItem key={e.key} value={e.key}>
                      <Checkbox checked={form.events.indexOf(e.key) > -1} />
                      <ListItemText primary={e.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                Leave empty to receive every event type.
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={form.enabled} onChange={(e) => setField('enabled', e.target.checked)} disabled={readOnly} />}
                label="Enabled"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Rate limit (requests / minute)"
                value={form.rpm}
                onChange={(e) => setField('rpm', e.target.value)}
                inputProps={{ min: 1, max: 6000 }}
                helperText="Advanced — defaults to 120."
                disabled={readOnly}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{readOnly ? 'Close' : 'Cancel'}</Button>
          {!readOnly && (
            <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>
              {editing ? 'Save changes' : 'Add destination'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete destination</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteTarget?.name}</strong>? Events will stop being forwarded there immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logs */}
      <Dialog open={logsOpen} onClose={() => setLogsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{logsTitle}</DialogTitle>
        <DialogContent>
          {logsLoading ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
          ) : logs.length === 0 ? (
            <Alert severity="info">No deliveries recorded yet.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Destination</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log._id} hover>
                      <TableCell>
                        <Tooltip title={log.createdAt ? new Date(log.createdAt).toLocaleString() : ''}>
                          <Typography variant="caption">{log.createdAt ? formatRelativeTime(log.createdAt) : '—'}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell><Typography variant="caption">{log.integrationName || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{log.eventType || log.kind || '—'}</Typography></TableCell>
                      <TableCell>
                        <Chip size="small" color={log.success ? 'success' : 'error'} label={log.success ? 'Delivered' : 'Failed'} />
                      </TableCell>
                      <TableCell><Typography variant="caption">{log.status || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{log.durationMs != null ? `${log.durationMs} ms` : '—'}</Typography></TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Tooltip title={log.error || log.response || ''}>
                          <Typography variant="caption" color={log.error ? 'error.main' : 'text.secondary'}>
                            {truncate(log.error || log.response || '', 60)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutboundDestinations;
